import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Card, Button } from './common';
import { theme } from '../styles/theme';
import BasicAuditInfo from './AuditSchedule/BasicAuditInfo';
import AuditScopeMethod from './AuditSchedule/AuditScopeMethod';
import ScheduleLocation from './AuditSchedule/ScheduleLocation';
import AuditorsVendors from './AuditSchedule/AuditorsVendors';
import StatusInfo from './AuditSchedule/StatusInfo';
import AuditScheduleTable from './AuditSchedule/AuditScheduleTable';

const initialFormState = {
  // Basic Audit Info
  frequency: '',
  activityLastPerformed: '',
  dueDate: '',
  auditorOrg: '',
  purpose: '',

  // Audit Scope & Method
  auditMethod: [],
  internalDepartment: [],
  internalScope: [],
  typeVendor: '',
  externalScope: [],
  applicableStandards: '',
  objective: '',

  // Schedule & Location
  scheduledDate: '',
  duration: '',
  location: '', // Text field for address/facility
  station: '', // IATA code
  stationDetails: null,
  logisticRequirement: [],
  confirmedAuditDate: '',

  // Auditors & Vendors
  leadAuditorInternal: '',
  leadAuditorEmail: '',
  leadAuditorNameExternal: '',
  leadAuditorEmailExternal: '',
  externalVendor: '',
  supplierCode: '',
  contactPoint: '',
  emailAddress: '',
  auditor2: '',
  auditor2Email: '',
  auditor3: '',
  auditor3Email: '',
  auditor4: '',
  auditor4Email: '',
  auditor5: '',
  auditor5Email: '',

  // Status & Additional Info
  scheduleStatus: 'Not Scheduled', // Set default status
  isDeferred: 'N',
  isAuditReportGenerated: 'N',
  isAuditScheduleCreated: 'N',
  checklist: '',
  remarks: '',
  reasonForDeferral: '',
  auditReportNumber: '',
  previousAuditReportLink: ''
};

const AuditSchedule = () => {
  const [view, setView] = useState('table');
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSectionChange = (sectionData, updatedErrors = null) => {
    console.log('Section data changed:', sectionData); // Debug log
    setFormData(prev => ({
      ...prev,
      ...sectionData
    }));

    if (updatedErrors !== null) {
      setErrors(updatedErrors);
    }
  };

  const validateForm = () => {
    console.log('Validating form data:', formData); // Debug log
    const newErrors = {};

    // Required fields validation
    const requiredFields = [
      'frequency',
      'dueDate',
      'auditorOrg',
      'purpose',
      'auditMethod',
      'applicableStandards',
      'objective',
      'scheduledDate',
      'duration',
      'location',
      'station',
      'scheduleStatus'
    ];

    requiredFields.forEach(field => {
      if (Array.isArray(formData[field])) {
        if (!formData[field].length) {
          newErrors[field] = 'This field is required';
        }
      } else if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    // Conditional validation
    if (formData.isDeferred === 'Y' && !formData.reasonForDeferral) {
      newErrors.reasonForDeferral = 'Reason for deferral is required when deferred';
    }

    console.log('Validation errors:', newErrors); // Debug log
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForSubmission = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData); // Debug log
    setSubmitError('');

    if (!validateForm()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Get location_id for the selected station
      const { data: stationData, error: stationError } = await supabase
        .from('line_station_information')
        .select('id')
        .eq('iata_code', formData.station)
        .single();

      if (stationError) {
        console.error('Error fetching station:', stationError);
        throw new Error('Failed to get station information');
      }

      if (!stationData) {
        throw new Error('Station not found');
      }

      // Prepare the data for submission
      const submissionData = {
        // Let the database trigger handle item generation
        frequency: formData.frequency,
        activity_last_performed: formatDateForSubmission(formData.activityLastPerformed),
        due_date: formatDateForSubmission(formData.dueDate),
        auditor_org: formData.auditorOrg,
        purpose: formData.purpose,
        
        audit_method: formData.auditMethod,
        internal_department: formData.internalDepartment,
        internal_scope: formData.internalScope,
        type_vendor: formData.typeVendor,
        external_scope: formData.externalScope,
        applicable_standards: formData.applicableStandards,
        objective: formData.objective,
        
        scheduled_date: formatDateForSubmission(formData.scheduledDate),
        duration: parseInt(formData.duration),
        location: formData.location,
        location_id: stationData.id,
        logistic_requirement: formData.logisticRequirement,
        confirmed_audit_date: formatDateForSubmission(formData.confirmedAuditDate),
        
        // Handle UUID fields - only include if they have valid values
        ...(formData.leadAuditorInternal && { lead_auditor_internal: formData.leadAuditorInternal }),
        lead_auditor_email: formData.leadAuditorEmail,
        lead_auditor_name_external: formData.leadAuditorNameExternal,
        lead_auditor_email_external: formData.leadAuditorEmailExternal,
        ...(formData.externalVendor && { external_vendor_id: formData.externalVendor }),
        auditor_2: formData.auditor2,
        auditor_2_email: formData.auditor2Email,
        auditor_3: formData.auditor3,
        auditor_3_email: formData.auditor3Email,
        auditor_4: formData.auditor4,
        auditor_4_email: formData.auditor4Email,
        auditor_5: formData.auditor5,
        auditor_5_email: formData.auditor5Email,
        
        schedule_status: formData.scheduleStatus,
        is_deferred: formData.isDeferred === 'Y',
        is_audit_report_generated: formData.isAuditReportGenerated === 'Y',
        is_audit_schedule_created: formData.isAuditScheduleCreated === 'Y',
        ...(formData.checklist && { checklist_id: formData.checklist }),
        remarks: formData.remarks,
        reason_for_deferral: formData.reasonForDeferral,
        audit_report_number: formData.auditReportNumber,
        previous_audit_report_link: formData.previousAuditReportLink
      };

      console.log('Submitting to Supabase:', submissionData); // Debug log

      const { data, error } = await supabase
        .from('audit_schedules')
        .insert([submissionData])
        .select();

      if (error) {
        console.error('Error inserting audit schedule:', error);
        throw error;
      }

      console.log('Successfully saved:', data); // Debug log

      // Reset form and switch to table view on success
      setFormData(initialFormState);
      setRefreshCounter(prev => prev + 1);
      setView('table');
    } catch (error) {
      console.error('Error saving audit schedule:', error); // Debug log
      const errorMessage = error.message || 'An error occurred while saving the audit schedule';
      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ color: theme.colors.primary.main }}>
          Audit Schedule
        </h2>
        <Button
          onClick={() => setView(view === 'table' ? 'form' : 'table')}
          variant="secondary"
        >
          {view === 'table' ? 'Create New Schedule' : 'View All Schedules'}
        </Button>
      </div>

      {view === 'table' ? (
        <Card>
          <div className="p-6">
            <AuditScheduleTable refreshTrigger={refreshCounter} />
          </div>
        </Card>
      ) : (
        <Card>
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <BasicAuditInfo 
                formData={formData}
                onChange={handleSectionChange}
                errors={errors}
              />

              <AuditScopeMethod 
                formData={formData}
                onChange={handleSectionChange}
                errors={errors}
              />

              <ScheduleLocation 
                formData={formData}
                onChange={handleSectionChange}
                errors={errors}
              />

              <AuditorsVendors 
                formData={formData}
                onChange={handleSectionChange}
                errors={errors}
              />

              <StatusInfo 
                formData={formData}
                onChange={handleSectionChange}
                errors={errors}
              />

              {submitError && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setView('table')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Audit Schedule'}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AuditSchedule;
